/**
 * /fr/backlog-produit — backlog concept page (FR). Targets backlog produit (20).
 * Links /fr/roadmap-produit + /fr/prd-document-exigences-produit.
 */
import { MarketingShell } from './MarketingShell';

export function FrBacklogProduit() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/backlog-produit"
      hreflangLocales={['fr']}
      documentTitle="Backlog produit : définition, priorisation, gestion · Draftlize"
      documentDescription="Le backlog produit expliqué : ce qu'il contient, comment le prioriser, et comment éviter qu'il devienne une décharge d'idées sans contexte."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Backlog</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 58px)', lineHeight: 1.04, margin: '12px 0 24px' }}>
            Le backlog produit,<br />
            <em>sans la décharge d'idées.</em>
          </h1>
          <p className="lede">
            Le backlog produit est la liste priorisée de ce qui reste à faire : fonctionnalités, améliorations, corrections. Bien tenu, c'est la file d'attente qui dit « quoi ensuite ». Mal tenu, il devient une <em>décharge d'idées</em> : des centaines d'items sans contexte, dont personne ne se rappelle le pourquoi. La différence tient à une chose — garder la décision et le « pourquoi » attachés à chaque item.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Prioriser un backlog produit.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>RICE</strong> — Reach, Impact, Confidence, Effort : un score pour comparer.</li>
            <li><strong>MoSCoW</strong> — Must / Should / Could / Won't : par niveau d'exigence.</li>
            <li><strong>Valeur vs effort</strong> — la matrice rapide pour trancher.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Quel que soit le cadre, le piège est le même : le score dit « quoi », jamais « pourquoi ». Reliez chaque item à la décision qui l'a fait remonter, et à la <a href="/fr/roadmap-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>roadmap</a> au-dessus.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">Backlog vs roadmap</span>
            <h2 className="serif">Deux objets différents, <em>souvent confondus.</em></h2>
          </div>
          <p>La roadmap répond « pourquoi / vers où » au niveau des résultats ; le backlog répond « quoi ensuite » au niveau du travail concret.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th className="ours">Backlog dans Draftlize</th>
              <th>Backlog-décharge</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Le « pourquoi » de l'item</td><td className="ours yes">carte de décision liée</td><td className="no">perdu / oublié</td></tr>
            <tr><td>La décision d'origine change</td><td className="ours yes">l'item passe « stale »</td><td className="no">l'item reste, faux</td></tr>
            <tr><td>Six mois d'items empilés</td><td className="ours yes">contexte conservé</td><td className="no">cimetière d'idées</td></tr>
            <tr><td>Lisible par un agent IA</td><td className="ours yes">via MCP</td><td className="partial">liste plate</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          Un backlog ne se noie pas sous le nombre d'items. Il se noie quand plus personne ne sait pourquoi ils y sont.
        </blockquote>
        <cite>Chaque item de backlog garde sa décision et son contexte. C'est Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Qui gère le backlog ?', a: 'En général le product manager (ou product owner en Scrum), qui le priorise et le maintient propre. L\'essentiel est que le « pourquoi » de chaque item reste retrouvable.' },
            { q: 'Backlog ou roadmap ?', a: 'Le backlog est la file d\'attente du travail concret ; la roadmap donne la direction au niveau des résultats. Le backlog découle de la roadmap et des décisions.' },
            { q: 'Combien d\'items un backlog doit-il contenir ?', a: 'Assez pour couvrir le court terme sans devenir une décharge. Au-delà, mieux vaut archiver que garder des items sans contexte.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un backlog qui garde son contexte <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Reliez chaque item de backlog à la décision qui l'a fait remonter. Quand cette décision change, l'item se signale — fini la décharge d'idées sans contexte.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
